import React, { useCallback, useEffect, useRef, useState } from 'react';
import useClawWsStore from '@src/stores/useClawWsStore';
import type { ToolCall } from '@src/stores/useClawWsStore';
import { useClawWs } from '@src/hooks/useClawWs';
import type { GatewayFrame } from '@src/services/claw-ws';
import './DebugPanel.less';

// ─── 类型 ─────────────────────────────────────────────────────────────────────

type Tab = 'status' | 'ws' | 'tools';

interface WsMessage {
  id: number;
  ts: string;
  frame: GatewayFrame;
  raw: string;
}

// ─── 日志行（折叠/展开） ──────────────────────────────────────────────────────

const LogLine: React.FC<{ line: string; index: number }> = ({ line, index }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = line.length > 120;
  const preview = isLong ? line.slice(0, 120) + '…' : line;

  return (
    <div
      className={`debug-panel__log-line${expanded ? ' debug-panel__log-line--expanded' : ''}`}
      onClick={() => isLong && setExpanded((v) => !v)}
      style={{ cursor: isLong ? 'pointer' : 'default' }}
    >
      <span className="debug-panel__log-index">{String(index + 1).padStart(4, ' ')}</span>
      <span className="debug-panel__log-text">{expanded ? line : preview}</span>
      {isLong && <span className="debug-panel__log-toggle">{expanded ? '▲' : '▼'}</span>}
    </div>
  );
};

// ─── WS 消息行（折叠/展开） ───────────────────────────────────────────────────

const WsLine: React.FC<{ msg: WsMessage }> = ({ msg }) => {
  const [expanded, setExpanded] = useState(false);
  const frame = msg.frame as Record<string, unknown>;
  const type = String(frame.type ?? '?');
  const method = String(frame.method ?? frame.event ?? '');
  const ok = 'ok' in frame ? (frame.ok ? '✓' : '✗') : '';
  const tag = [type, method, ok].filter(Boolean).join(' · ');

  const typeColor =
    type === 'event' ? '#89b4fa' : type === 'res' ? '#a6e3a1' : '#cdd6f4';

  return (
    <div
      className={`debug-panel__ws-line${expanded ? ' debug-panel__ws-line--expanded' : ''}`}
      onClick={() => setExpanded((v) => !v)}
    >
      <span className="debug-panel__ws-ts">{msg.ts}</span>
      <span className="debug-panel__ws-tag" style={{ color: typeColor }}>{tag}</span>
      <span className="debug-panel__log-toggle">{expanded ? '▲' : '▼'}</span>
      {expanded && (
        <pre className="debug-panel__ws-body">{msg.raw}</pre>
      )}
    </div>
  );
};

// ─── 工具调用组件 ─────────────────────────────────────────────────────────────

const TOOL_STATUS_ICON: Record<ToolCall['status'], string> = {
  running: '⏳',
  done: '✅',
  error: '❌',
};

const ToolCallItem: React.FC<{ call: ToolCall }> = ({ call }) => {
  const [expanded, setExpanded] = useState(false);
  const duration =
    call.finishedAt != null
      ? `${((call.finishedAt - call.startedAt) / 1000).toFixed(2)}s`
      : null;

  return (
    <div
      className={`debug-panel__tool-item debug-panel__tool-item--${call.status}`}
      onClick={() => setExpanded((v) => !v)}
    >
      <div className="debug-panel__tool-header">
        <span className="debug-panel__tool-status-icon">{TOOL_STATUS_ICON[call.status]}</span>
        <span className="debug-panel__tool-name">{call.name}</span>
        {call.meta && <span className="debug-panel__tool-meta">{call.meta}</span>}
        {duration && <span className="debug-panel__tool-duration">{duration}</span>}
        <span className="debug-panel__log-toggle">{expanded ? '▲' : '▼'}</span>
      </div>
      {expanded && (
        <div className="debug-panel__tool-body">
          {call.args != null && (
            <div className="debug-panel__tool-section">
              <div className="debug-panel__tool-section-label">入参</div>
              <pre className="debug-panel__tool-code">{JSON.stringify(call.args, null, 2)}</pre>
            </div>
          )}
          {call.result != null && (
            <div className="debug-panel__tool-section">
              <div className="debug-panel__tool-section-label">结果</div>
              <pre className="debug-panel__tool-code">
                {typeof call.result === 'string'
                  ? call.result
                  : JSON.stringify(call.result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── DebugPanel ───────────────────────────────────────────────────────────────

const MAX_WS_MSGS = 200;
let _wsSeq = 0;

const getBaseKey = (key: string) => key.split(':').pop() ?? key;

const DebugPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { connStatus, helloOk, toolCalls, currentSessionKey } = useClawWsStore();
  const { fetchLogs, subscribeFrames } = useClawWs();

  const sessionToolCalls = toolCalls
    .filter((c) => getBaseKey(c.sessionKey) === getBaseKey(currentSessionKey ?? ''))
    .sort((a, b) => a.startedAt - b.startedAt);
  const runningCount = sessionToolCalls.filter((c) => c.status === 'running').length;

  const [tab, setTab] = useState<Tab>('status');
  const [logs, setLogs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const [logCopied, setLogCopied] = useState(false);
  const [wsMsgs, setWsMsgs] = useState<WsMessage[]>([]);
  const [wsPaused, setWsPaused] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [pos, setPos] = useState({ x: window.innerWidth - 504, y: 80 });

  const logBodyRef = useRef<HTMLDivElement>(null);
  const wsBodyRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null);
  const wsPausedRef = useRef(false);
  wsPausedRef.current = wsPaused;

  // ── 拖拽 ──────────────────────────────────────────────────────────────────

  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    dragState.current = { startX: e.clientX, startY: e.clientY, startPosX: pos.x, startPosY: pos.y };
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragState.current) return;
      const dx = e.clientX - dragState.current.startX;
      const dy = e.clientY - dragState.current.startY;
      const panel = panelRef.current;
      const maxX = window.innerWidth - (panel?.offsetWidth ?? 480);
      const maxY = window.innerHeight - (panel?.offsetHeight ?? 60);
      setPos({
        x: Math.max(0, Math.min(maxX, dragState.current.startPosX + dx)),
        y: Math.max(0, Math.min(maxY, dragState.current.startPosY + dy)),
      });
    };
    const onMouseUp = () => { dragState.current = null; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // ── WS 帧监听 ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const unsub = subscribeFrames((frame) => {
      if (wsPausedRef.current) return;
      const entry: WsMessage = {
        id: ++_wsSeq,
        ts: new Date().toLocaleTimeString('zh-CN', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        frame,
        raw: JSON.stringify(frame, null, 2),
      };
      setWsMsgs((prev) => {
        const next = [...prev, entry];
        return next.length > MAX_WS_MSGS ? next.slice(next.length - MAX_WS_MSGS) : next;
      });
      // 自动滚底
      requestAnimationFrame(() => {
        if (wsBodyRef.current) wsBodyRef.current.scrollTop = wsBodyRef.current.scrollHeight;
      });
    });
    return unsub;
  }, [subscribeFrames]);

  // ── 日志 ──────────────────────────────────────────────────────────────────

  const handleFetchLogs = useCallback(async () => {
    setLoading(true);
    setLogError(null);
    try {
      const result = await fetchLogs(200);
      setLogs(result?.lines ?? []);
      requestAnimationFrame(() => {
        if (logBodyRef.current) logBodyRef.current.scrollTop = logBodyRef.current.scrollHeight;
      });
    } catch (e) {
      setLogError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [fetchLogs]);

  const handleCopyLogs = useCallback(() => {
    navigator.clipboard.writeText(logs.join('\n')).then(() => {
      setLogCopied(true);
      setTimeout(() => setLogCopied(false), 2000);
    });
  }, [logs]);

  // ── 状态色 ────────────────────────────────────────────────────────────────

  const statusColor =
    connStatus === 'connected' ? '#a6e3a1' :
    connStatus === 'connecting' ? '#f9e2af' : '#f38ba8';

  return (
    <div
      ref={panelRef}
      className={`debug-panel${minimized ? ' debug-panel--minimized' : ''}`}
      style={{ left: pos.x, top: pos.y }}
    >
      {/* ── 标题栏 ── */}
      <div className="debug-panel__header" onMouseDown={onMouseDown}>
        <span className="debug-panel__title">🛠 调试面板</span>
        <div className="debug-panel__header-actions">
          <button className="debug-panel__btn" onClick={() => setMinimized((v) => !v)} title={minimized ? '展开' : '最小化'} type="button">
            {minimized ? '▲' : '▼'}
          </button>
          <button className="debug-panel__btn" onClick={onClose} title="关闭" type="button">✕</button>
        </div>
      </div>

      {!minimized && (
        <>
          {/* ── Tab 栏 ── */}
          <div className="debug-panel__tabs">
            <button className={`debug-panel__tab${tab === 'status' ? ' debug-panel__tab--active' : ''}`} onClick={() => setTab('status')} type="button">
              WS 状态 &amp; 日志
            </button>
            <button className={`debug-panel__tab${tab === 'ws' ? ' debug-panel__tab--active' : ''}`} onClick={() => setTab('ws')} type="button">
              消息流
              {wsMsgs.length > 0 && <span className="debug-panel__tab-badge">{wsMsgs.length}</span>}
            </button>
            <button className={`debug-panel__tab${tab === 'tools' ? ' debug-panel__tab--active' : ''}`} onClick={() => setTab('tools')} type="button">
              工具调用
              {runningCount > 0 ? (
                <span className="debug-panel__tab-badge debug-panel__tab-badge--running">{runningCount}</span>
              ) : sessionToolCalls.length > 0 ? (
                <span className="debug-panel__tab-badge">{sessionToolCalls.length}</span>
              ) : null}
            </button>
          </div>

          {/* ── Tab: WS 状态 & 日志 ── */}
          {tab === 'status' && (
            <>
              <div className="debug-panel__section">
                <div className="debug-panel__section-title">WebSocket 状态</div>
                <div className="debug-panel__status-row">
                  <span className="debug-panel__status-dot" style={{ background: statusColor }} />
                  <span className="debug-panel__status-text">{connStatus}</span>
                </div>
                {helloOk && (
                  <div className="debug-panel__meta">
                    <div>协议版本：{helloOk.protocol}</div>
                    <div>connId：{helloOk.server?.connId}</div>
                    <div>scopes：{helloOk.auth?.scopes?.join(', ') ?? '—'}</div>
                  </div>
                )}
              </div>

              <div className="debug-panel__section debug-panel__section--logs">
                <div className="debug-panel__section-header">
                  <span className="debug-panel__section-title" style={{ marginBottom: 0 }}>
                    网关日志 (logs.tail)
                    {logs.length > 0 && <span className="debug-panel__log-count"> · {logs.length} 行</span>}
                  </span>
                  <div className="debug-panel__log-actions">
                    {logs.length > 0 && (
                      <button className="debug-panel__fetch-btn" onClick={handleCopyLogs} type="button">
                        {logCopied ? '已复制 ✓' : '复制全部'}
                      </button>
                    )}
                    <button className="debug-panel__fetch-btn" onClick={handleFetchLogs} disabled={loading} type="button">
                      {loading ? '加载中…' : '拉取'}
                    </button>
                  </div>
                </div>
                {logError && <div className="debug-panel__error">{logError}</div>}
                <div className="debug-panel__log-body" ref={logBodyRef}>
                  {logs.length === 0 && !loading && !logError && (
                    <div className="debug-panel__log-empty">点击「拉取」查看最新日志</div>
                  )}
                  {logs.map((line, i) => <LogLine key={i} line={line} index={i} />)}
                </div>
              </div>
            </>
          )}

          {/* ── Tab: WS 消息流 ── */}
          {tab === 'ws' && (
            <div className="debug-panel__section debug-panel__section--logs">
              <div className="debug-panel__section-header">
                <span className="debug-panel__section-title" style={{ marginBottom: 0 }}>
                  实时消息
                  {wsMsgs.length > 0 && <span className="debug-panel__log-count"> · {wsMsgs.length}/{MAX_WS_MSGS}</span>}
                </span>
                <div className="debug-panel__log-actions">
                  <button className="debug-panel__fetch-btn" onClick={() => setWsPaused((v) => !v)} type="button">
                    {wsPaused ? '▶ 继续' : '⏸ 暂停'}
                  </button>
                  <button className="debug-panel__fetch-btn" onClick={() => setWsMsgs([])} type="button">
                    清空
                  </button>
                </div>
              </div>
              <div className="debug-panel__log-body" ref={wsBodyRef}>
                {wsMsgs.length === 0 && (
                  <div className="debug-panel__log-empty">等待 WS 消息…</div>
                )}
                {wsMsgs.map((msg) => <WsLine key={msg.id} msg={msg} />)}
              </div>
            </div>
          )}

          {/* ── Tab: 工具调用 ── */}
          {tab === 'tools' && (
            <div className="debug-panel__section debug-panel__section--logs">
              <div className="debug-panel__section-header">
                <span className="debug-panel__section-title" style={{ marginBottom: 0 }}>
                  当前 run 工具调用
                  {sessionToolCalls.length > 0 && (
                    <span className="debug-panel__log-count"> · {sessionToolCalls.length} 条</span>
                  )}
                  {runningCount > 0 && (
                    <span className="debug-panel__log-count debug-panel__log-count--running"> · {runningCount} 运行中</span>
                  )}
                </span>
              </div>
              <div className="debug-panel__log-body">
                {sessionToolCalls.length === 0 ? (
                  <div className="debug-panel__log-empty">本次 run 暂无工具调用记录</div>
                ) : (
                  sessionToolCalls.map((c) => <ToolCallItem key={c.toolCallId} call={c} />)
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DebugPanel;
